/// <amd-module name="DistributedTaskUI/Logs/PhaseLogsDetailsView" />
import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/PhaseLogsDetailsView";

import { AgentAcquisition, IAgentAcquisitionBaseProps } from "DistributedTaskUI/Logs/AgentAcquisition";
import { IssuesComponent } from "DistributedTaskUI/Logs/IssuesComponent";
import { IPhaseJobItem, Issue, JobStates, JobType } from "DistributedTaskUI/Logs/Logs.Types";
import { LogsViewUtility } from "DistributedTaskUI/Logs/LogsViewUtility";
import { StepComponent } from "DistributedTaskUI/Logs/StepComponent";
import { IPhaseSummaryViewProps, PhaseSummaryView } from "DistributedTaskUI/Logs/PhaseSummaryView";
import {
    ITaskListLogsControllerViewProps,
    TaskListLogsControllerView,
} from "DistributedTaskUI/Logs/TaskListLogsControllerView";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { css } from "VSSUI/Util";
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent } from "VSS/Platform/Layout";
import { VssIconType } from "VSSUI/VssIcon";

export interface IPhaseLogsDetailsViewProps extends ITaskListLogsControllerViewProps, IAgentAcquisitionBaseProps {
    // Phase job Item
    item: IPhaseJobItem;
    // Issues related to phase
    issues?: Issue[];
    // Optional css class name
    cssClass?: string;
    // page context for the current page
    pageContext?: IVssPageContext;
    // task definition id to help url mapping
    taskDefinitionIdToHelpUrlMap?: { [id: string]: string };
}

const LogsViewUtility2 = LogsViewUtility; // This is for decreasing size of the minified script

/**
 * Base view for agent and agentless Phase
 */
export abstract class PhaseLogsDetailsView<P extends IPhaseLogsDetailsViewProps> extends VssComponent<P, {}> {

    // There are couple of cases exist with tasks and issues object
    // 1) Phase has no tasks but has issues (errors, warning) -> User should see issues.
    // 2) Phase has no tasks and there are no issues -> User should see job state details
    // 3) Phase has tasks and also has issues (errors, warning) -> User should issues as well as job information having all task details
    // 4) Phase has tasks and there are no issues -> User should see all task details for the job
    public render(): React.ReactNode {
        const phaseItem = this.props.item;
        const jobState = (phaseItem && phaseItem.jobState) ? phaseItem.jobState : JobStates.Pending;

        const phaseSummaryProperties: IPhaseSummaryViewProps | null = this._getPhaseSummaryProperties();
        if (phaseSummaryProperties) {
            if (this.props.item && this.props.item.jobType === JobType.AgentPhaseJob && jobState === JobStates.Pending) {
                const pageContext = this.context.pageContext || this.props.pageContext;
                return (
                    <AgentAcquisition
                        queueId={phaseSummaryProperties.queueId}
                        agentName={phaseSummaryProperties.agentName}
                        jobRequest={this.props.jobRequest}
                        licenseInfo={this.props.licenseInfo}
                        queuePosition={this.props.queuePosition}
                        isHostedPool={this.props.isHostedPool}
                        isAnonymousUser={this.props.isAnonymousUser}
                        hideLicenseInfoSection={this.props.hideLicenseInfoSection}
                        pageContext={pageContext}
                    />
                );
            } else {
                let taskIndexToScrollOnMount;
                const tasksExist = this._areThereTasks();
                const issuesExist = this._areThereIssues();

                if (this.props.indexToScrollOnMount && this.props.indexToScrollOnMount >= 0) {
                    taskIndexToScrollOnMount = this.props.indexToScrollOnMount;
                } else {
                    taskIndexToScrollOnMount = phaseItem ? LogsViewUtility2.getIndexOfTaskToBeMovedToView(jobState, phaseItem.tasks) : -1;
                }

                const pageContext = this.context.pageContext || this.props.pageContext;
                const taskListProps = { ...this.props as IPhaseLogsDetailsViewProps };
                taskListProps.pageContext = pageContext;
                return (
                    <div className={css(this._containerClassName, this.props.cssClass, "phase-logs-details-view")}>
                        <PhaseSummaryView {...phaseSummaryProperties} />
                        {issuesExist && this._renderIssuesComponent()}
                        {!tasksExist && !issuesExist && this._renderPhaseStateStepComponent(jobState)}
                        {tasksExist &&
                            <TaskListLogsControllerView {...taskListProps} indexToScrollOnMount={taskIndexToScrollOnMount} />}
                    </div>
                );
            }
        }

        return null;
    }

    private _areThereTasks(): boolean {
        return this.props.tasks && this.props.tasks.length > 0;
    }

    private _areThereIssues(): boolean {
        return (this.props.issues && this.props.issues.length > 0) || false;
    }

    protected _renderIssuesComponent(): React.ReactNode {
        if (this.props.issues) {
            return (
                <div className="logs-issues-list-container">
                    <IssuesComponent
                        headerText={Resources.PhaseIssuesHeaderText}
                        issues={this.props.issues}
                        pageContext={this.context.pageContext || this.props.pageContext} />
                </div>
            );
        }

        return null;
    }

    private _renderPhaseStateStepComponent(jobState: JobStates): React.ReactNode {
        if (jobState) {
            const phaseItem = this.props.item;
            const tooltipContent = phaseItem && phaseItem.resultCode;
            const status = LogsViewUtility2.getPhaseStatusFromJobStates(jobState);
            if (status) {
                return (
                    <div className="logs-phase-error-step-component-container" data-is-focusable={true} role="status" aria-live={"assertive"}>
                        <StepComponent
                            isTitleClickable={false}
                            title={status.status}
                            infoProps={{ tooltipContent: tooltipContent, iconName: "Info", iconAriaLabel: Resources.Info }}
                            // Remove iconProps in M143
                            iconProps={{ className: css("logs-phase-error-step-status-icon", "log-task-state", status.className), iconName: status.iconName, iconType: VssIconType.fabric }}
                            {...status.statusProps ? {
                                statusProps: {
                                    statusProps: status.statusProps
                                }
                            } : {}}
                            cssClass={"logs-phase-error-step-component-content"}
                            ariaLabel={status.status}>
                        </StepComponent>
                    </div>
                );
            }
        }

        return null;
    }

    protected abstract _getPhaseSummaryProperties(): IPhaseSummaryViewProps | null;
    protected abstract _containerClassName: string;
}
